import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';

/**
 * Geocode addresses using the GeoComm/Esri Suggest and FindAddressCandidates endpoints.
 * Uses a two-step flow: /suggest for type-ahead, then /findAddressCandidates with magicKey.
 * https://developers.arcgis.com/rest/geocode/api-reference/geocoding-suggest.htm
 * @param {String} serviceUrl - A GIS or Esri geocode service url
 * @param {String} searchTerm - input value in ConnectedUniversalSearch
 * @param {Number} latitude - The location's latitude
 * @param {Number} longitude - The location's longitude
 */

const isValidSuggestions = result => {
  if (
    result?.data?.error?.code > 299 ||
    result?.data?.error?.code < 200 ||
    !result?.data?.suggestions?.length
  ) {
    return false;
  }
  return true;
};

const isValidCandidates = result => {
  if (
    result?.data?.error?.code > 299 ||
    result?.data?.error?.code < 200 ||
    !result?.data?.candidates?.length
  ) {
    return false;
  }
  return true;
};

export const getSuggestedAddressesApi = createApi({
  reducerPath: 'getSuggestedAddressesApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),

  endpoints: builder => ({
    getSuggestedAddresses: builder.query({
      queryFn: async (
        {
          searchTerm,
          latitude,
          longitude,
          token,
          serviceUrl,
          countryCode = 'USA',
        },
        _queryApi,
        _extraOptions,
      ) => {
        const MAX_RESULTS = 10;
        const params = {
          text: searchTerm,
          countryCode,
          location: `${longitude},${latitude}`,
          maxSuggestions: MAX_RESULTS,
          f: 'json',
        };

        let localSuggestions = [];

        // Try local geocoder first
        if (serviceUrl) {
          try {
            const localResult = await fetchBaseQuery({
              baseUrl: serviceUrl,
            })(
              {
                url: `/suggest`,
                method: 'GET',
                params: { ...params, token },
              },
              _queryApi,
              _extraOptions,
            );

            if (isValidSuggestions(localResult)) {
              localSuggestions = (localResult.data?.suggestions || []).map(
                s => ({
                  ...s,
                  _serviceUrl: serviceUrl,
                  _token: token,
                }),
              );
            }
          } catch {
            // Continue to ESRI
          }
        }

        // If we have enough results, return them
        if (localSuggestions.length >= MAX_RESULTS) {
          return { data: localSuggestions.slice(0, MAX_RESULTS) };
        }

        // Otherwise, supplement with ESRI world geocoder results
        let esriSuggestions = [];
        try {
          const remaining = MAX_RESULTS - localSuggestions.length;
          const esriResult = await fetchBaseQuery({
            baseUrl: ESRI_GEOCODE_SERVICE_URL,
          })(
            {
              url: `/suggest`,
              method: 'GET',
              params: { ...params, maxSuggestions: remaining },
            },
            _queryApi,
            _extraOptions,
          );

          if (isValidSuggestions(esriResult)) {
            esriSuggestions = (esriResult.data?.suggestions || []).map(s => ({
              ...s,
              _serviceUrl: ESRI_GEOCODE_SERVICE_URL,
              _token: null,
            }));
          }
        } catch {
          // ESRI failed, continue with local results only
        }

        // Combine local + ESRI results (local first)
        const combined = [...localSuggestions, ...esriSuggestions].slice(
          0,
          MAX_RESULTS,
        );
        return { data: combined };
      },
      keepUnusedDataFor: 3600,
    }),

    getAddressFromMagicKey: builder.query({
      queryFn: async (
        { magicKey, _serviceUrl, _token },
        _queryApi,
        _extraOptions,
      ) => {
        const baseUrl = _serviceUrl || ESRI_GEOCODE_SERVICE_URL;
        const params = {
          magicKey,
          outFields: '*',
          f: 'json',
        };

        if (_token) {
          params.token = _token;
        }

        const result = await fetchBaseQuery({
          baseUrl,
        })(
          {
            url: `/findAddressCandidates`,
            method: 'GET',
            params,
          },
          _queryApi,
          _extraOptions,
        );

        if (isValidCandidates(result)) {
          const candidate = result.data?.candidates?.[0];
          return { data: candidate || null };
        }

        return { error: 'No address found for the given magicKey' };
      },
    }),

    // Legacy endpoint using findAddressCandidates directly (for Avive flow)
    getAddressCandidates: builder.query({
      queryFn: async (
        {
          searchTerm,
          latitude,
          longitude,
          token,
          serviceUrl,
          countryCode = 'USA',
        },
        _queryApi,
        _extraOptions,
      ) => {
        const params = {
          address: searchTerm,
          countryCode,
          location: `${longitude}, ${latitude}`,
          maxLocations: 10,
          outFields: '*',
          f: 'json',
        };

        if (serviceUrl) {
          try {
            const localResult = await fetchBaseQuery({
              baseUrl: serviceUrl,
            })(
              {
                url: `/findAddressCandidates`,
                method: 'GET',
                params: { ...params, token },
              },
              _queryApi,
              _extraOptions,
            );

            if (isValidCandidates(localResult)) {
              return { data: localResult.data?.candidates || [] };
            }
          } catch {
            // Fall through to ESRI
          }
        }

        const esriResult = await fetchBaseQuery({
          baseUrl: ESRI_GEOCODE_SERVICE_URL,
        })(
          {
            url: `/findAddressCandidates`,
            method: 'GET',
            params,
          },
          _queryApi,
          _extraOptions,
        );

        if (isValidCandidates(esriResult)) {
          return { data: esriResult.data?.candidates || [] };
        }

        return { data: [] };
      },
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useGetSuggestedAddressesQuery,
  useLazyGetAddressFromMagicKeyQuery,
  useGetAddressCandidatesQuery,
} = getSuggestedAddressesApi;

export const getAddressFromMagicKey =
  getSuggestedAddressesApi.endpoints.getAddressFromMagicKey;

export const getAddressCandidates =
  getSuggestedAddressesApi.endpoints.getAddressCandidates;

export default getSuggestedAddressesApi.endpoints.getSuggestedAddresses;
